import { FeatLineRidesSearch, useRidesSearchContext } from '@ridehub/feat-ride-search';
import { PageContainer, Pager, PagerArrow, PagerItem, PagerItemComp } from '@flixbus/honeycomb-react';
import React, { useState } from 'react';

import { RidesTable } from './rides-table';
import { TableSkeleton } from './table-skeleton';
import { TrainIllustration } from './train-illustrations';
import styles from './feat-ride-list.module.scss';
import { useRideSummarylist } from '@ridehub/data-ride';

export interface FeatRideListProps {
    auth?: boolean;
    getDetailsPath: (rideId: string) => string;
    isTest?: boolean;
}

export function FeatRideList(props: FeatRideListProps) {
    const authenticateRequests = props.auth === false ? false : true;
    const ridesSearchCtx = useRidesSearchContext();

    const [currPage, setCurrPage] = useState<number>(0);
    const maxPageSize = 15;
    const remainingRows = ridesSearchCtx.ridesSearchResults.length % maxPageSize; // after filling pages
    const fullPages = Math.floor(ridesSearchCtx.ridesSearchResults.length / maxPageSize);
    const hasPartialPage = remainingRows > 0;
    const totalPageCount = hasPartialPage ? fullPages + 1 : fullPages;
    const currPageSize = currPage === totalPageCount - 1 && hasPartialPage ? remainingRows : maxPageSize;
    const rideListPageSlice = ridesSearchCtx.ridesSearchResults.slice(
        currPage * maxPageSize,
        currPage * maxPageSize + maxPageSize
    );
    const { isPending, data, error } = useRideSummarylist(rideListPageSlice, authenticateRequests);

    if (error) {
        return <span>Error occurred</span>;
    }

    function PageControls(props: { extraClasses?: string }) {
        return (
            <Pager aria-label="Pagination" extraClasses={props.extraClasses}>
                <PagerArrow
                    onClick={() => {
                        if (currPage > 0) {
                            setCurrPage(currPage - 1);
                        }
                    }}
                    side="prev"
                    aria-label="previous page"
                />
                {
                    Array(totalPageCount)
                        .fill(undefined)
                        .map((val, idx) => {
                            return (
                                <PagerItem
                                    key={idx}
                                    onClick={() => {
                                        setCurrPage(idx);
                                    }}
                                    active={currPage === idx}
                                >
                                    {idx + 1}
                                </PagerItem>
                            );
                        }) as unknown as PagerItemComp
                }
                <PagerArrow
                    onClick={() => {
                        if (currPage < totalPageCount - 1) {
                            setCurrPage(currPage + 1);
                        }
                    }}
                    side="next"
                    aria-label="next page"
                />
            </Pager>
        );
    }

    const hasRides = ridesSearchCtx.ridesSearchResults.length > 0;
    return (
        <div>
            <div className={styles.searchSection}>
                <PageContainer>
                    <FeatLineRidesSearch auth={authenticateRequests} />
                </PageContainer>
            </div>

            <PageContainer>
                <div className={styles.bottomSpacer}>
                    {hasRides ? (
                        <div>
                            <h3>{`${ridesSearchCtx.ridesSearchResults.length} Rides`}</h3>
                            {isPending ? (
                                <TableSkeleton rowCount={currPageSize} />
                            ) : (
                                <RidesTable isTest={props.isTest} data={data} getDetailsPath={props.getDetailsPath} />
                            )}
                            <PageControls extraClasses={styles.pageControls} />
                        </div>
                    ) : ridesSearchCtx.emptySearchResults ? (
                        <h4>No search results</h4>
                    ) : (
                        <h4>Select line to search</h4>
                    )}
                    <TrainIllustration />
                </div>
            </PageContainer>
        </div>
    );
}
